import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import AddPaymentModal from './AddPaymentModal';
import ViewPaymentsModal from './ViewPaymentsModal';
import ViewPurchaseReturnModal from './ViewPurchaseReturnModal';
import invoiceGenerator from '../../services/invoiceGenerator';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import './Products.css';
import './ProductsActions.css';
import './AllSellStyles.css';
import './MobileResponsive.css';

const paymentStatusMap = {
  paid: { label: 'Paid', className: 'badge badge-success' },
  due: { label: 'Due', className: 'badge badge-warning' },
  partial: { label: 'Partial', className: 'badge badge-info' },
  overdue: { label: 'Overdue', className: 'badge badge-danger' },
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
};

const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  return num.toFixed(2);
};

const ListPurchaseReturn = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [perPage, setPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    location_id: '',
    supplier_id: '',
    start_date: '',
    end_date: '',
  });
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [openActionId, setOpenActionId] = useState(null);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showViewPaymentsModal, setShowViewPaymentsModal] = useState(false);
  const [viewReturnId, setViewReturnId] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);

  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });
  const actionButtonRefs = useRef({});

  useEffect(() => {
    if (!openActionId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.action-btn-trigger') || e.target.closest('.action-dropdown-menu')) return;
      setOpenActionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  const toggleDropdown = (rowId, event) => {
    event.stopPropagation();
    if (openActionId === rowId) {
      setOpenActionId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const gap = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuMinWidth = 220;
      const edgePadding = 8;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
      const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

      const left = Math.min(
        Math.max(rect.left, edgePadding),
        Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
      );

      setDropdownPosition({
        top: openUpwards ? null : (rect.bottom + gap),
        bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
        left,
        maxHeight
      });
      setOpenActionId(rowId);
    }
  };

  const fetchData = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = {
        page,
        per_page: perPage,
        search: searchTerm,
      };
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.supplier_id) params.supplier_id = filters.supplier_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const result = await apiService.getPurchaseReturns(params);
      if (result.success) {
        setData(result.data || []);
        setTotal(result.total || 0);
        setCurrentPage(result.current_page || 1);
        setLastPage(result.last_page || 1);
      } else {
        setError(result.message || 'Failed to load purchase returns');
      }
    } catch (err) {
      setError(err.message || 'Failed to load purchase returns');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await apiService.getLocations();
      const list = Array.isArray(res) ? res : (res.data || []);
      setLocations(list);
    } catch (err) {
      /* console.error('Error fetching locations:', err); */
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await apiService.getContacts({ type: 'supplier', per_page: 100 });
      const list = Array.isArray(res) ? res : (res.data || []);
      setSuppliers(list);
    } catch (err) {
      /* console.error('Error fetching suppliers:', err); */
    }
  };

  useEffect(() => {
    fetchLocations();
    fetchSuppliers();
    fetchData(1);
  }, []);

  useEffect(() => {
    fetchData(1);
  }, [perPage, filters]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(1), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      location_id: '',
      supplier_id: '',
      start_date: '',
      end_date: '',
    });
    setSearchTerm('');
  };

  const handleExport = (format) => {
    if (data.length === 0) {
      alert('No data available to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['Date', 'Reference No', 'Parent Purchase', 'Location', 'Supplier', 'Payment Status', 'Grand Total', 'Payment Due'];
    const rows = data.map((r) => [
      formatDate(r.transaction_date),
      r.ref_no || '',
      r.parent_purchase || '',
      r.location_name || '',
      `${r.name || ''} ${r.supplier_business_name || ''}`.trim(),
      r.payment_status || '',
      formatCurrency(r.final_total),
      formatCurrency((parseFloat(r.final_total) || 0) - (parseFloat(r.amount_paid) || 0)),
    ]);

    const title = 'Purchase Returns';
    const filename = 'purchase_returns';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this purchase return?')) return;
    try {
      const result = await apiService.deletePurchaseReturn(id);
      if (result.success) {
        fetchData(currentPage);
        toast.success('Purchase return deleted successfully.');
      } else {
        toast.error(result.message || 'Failed to delete purchase return');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete purchase return');
    }
  };

  const handleEdit = (row) => {
    setOpenActionId(null);
    navigate(`/purchase-return/edit/${row.id}`);
  };

  const handleViewPayments = async (row) => {
    setOpenActionId(null);
    try {
      const res = await apiService.getPayments(row.id);
      const list = Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
      const amountPaid = list.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
      const finalTotal = parseFloat(row.final_total) || 0;
      const due = Math.max(0, finalTotal - amountPaid);

      setSelectedReturn({
        ...row,
        id: row.id,
        ref_no: row.ref_no,
        contact: { name: `${row.name || ''} ${row.supplier_business_name || ''}`.trim() || 'Supplier' },
        location: { name: row.location_name || '--' },
        final_total: finalTotal,
        payment_due: due,
        payment_status: row.payment_status,
        payment_lines: list,
      });
      setShowViewPaymentsModal(true);
    } catch (err) {
      /* console.error('Error loading payments:', err); */
      toast.error('Failed to load payments');
    }
  };

  const handleAddPayment = (row) => {
    setOpenActionId(null);
    const amountPaid = parseFloat(row.amount_paid) || 0;
    const finalTotal = parseFloat(row.final_total) || 0;
    const due = Math.max(0, finalTotal - amountPaid);
    setSelectedReturn({
      id: row.id,
      customer: `${row.name || ''} ${row.supplier_business_name || ''}`.trim() || 'Supplier',
      invoice_no: row.ref_no,
      location_id: row.location_id,
      final_total: finalTotal,
      payment_due: due,
    });
    setShowAddPaymentModal(true);
  };

  const handlePrint = (row) => {
    setOpenActionId(null);
    const amountPaid = parseFloat(row.amount_paid) || 0;
    const finalTotal = parseFloat(row.final_total) || 0;
    const due = Math.max(0, finalTotal - amountPaid);
    invoiceGenerator.printInvoicePDF({
      invoiceNumber: row.ref_no,
      invoiceDate: formatDate(row.transaction_date),
      customerName: `${row.name || ''} ${row.supplier_business_name || ''}`.trim() || 'Supplier',
      items: (row.products || []).map((p) => ({
        sku: p.sub_sku || '',
        name: p.product_name || p.name || 'Product',
        quantity: parseFloat(p.quantity || 0),
        unitPrice: parseFloat(p.unit_price || 0),
        total: parseFloat(p.quantity || 0) * parseFloat(p.unit_price || 0),
      })),
      subtotal: parseFloat(row.total_before_tax || row.final_total || 0),
      tax: parseFloat(row.tax_amount || 0),
      total: finalTotal,
      totalPaid: amountPaid,
      amountDue: due,
    });
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > lastPage) return;
    fetchData(page);
  };

  const renderStatusBadge = (statusText) => {
    if (!statusText || statusText === 'N/A') {
      return (
        <span className="payment-status-badge" style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.26rem 0.72rem',
          borderRadius: '20px',
          fontSize: '0.78rem',
          fontWeight: '700',
          textTransform: 'uppercase',
          color: '#ffffff',
          letterSpacing: '0.2px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #94a3b8, #64748b)'
        }}>
          N/A
        </span>
      );
    }

    const s = String(statusText).toLowerCase();
    let bg = 'linear-gradient(135deg, #0284c7, #0369a1)';

    if (s === 'paid' || s === 'completed' || s === 'received' || s === 'delivered') {
      bg = 'linear-gradient(135deg, #10b981, #059669)';
    } else if (s === 'due' || s === 'partial' || s === 'partially_received') {
      bg = 'linear-gradient(135deg, #f59e0b, #d97706)';
    } else if (s === 'overdue' || s === 'cancelled') {
      bg = 'linear-gradient(135deg, #ef4444, #dc2626)';
    } else if (s === 'ordered' || s === 'pending' || s === 'draft') {
      bg = 'linear-gradient(135deg, #0284c7, #0369a1)';
    }

    const formattedText = String(statusText).toUpperCase();

    return (
      <span className="payment-status-badge" style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.26rem 0.72rem',
        borderRadius: '20px',
        fontSize: '0.78rem',
        fontWeight: '700',
        textTransform: 'uppercase',
        color: '#ffffff',
        letterSpacing: '0.2px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        background: bg
      }}>
        {formattedText}
      </span>
    );
  };

  return (
    <div className="products-container">
      <div className="main-card">
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={() => navigate('/purchases/return/add')}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="filter-panel card p-3 mb-3">
            <div className="row">
              <div className="col-md-3 mb-2">
                <label>Location</label>
                <select name="location_id" className="form-control" value={filters.location_id} onChange={handleFilterChange}>
                  <option value="">All Locations</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-2">
                <label>Supplier</label>
                <select name="supplier_id" className="form-control" value={filters.supplier_id} onChange={handleFilterChange}>
                  <option value="">All Suppliers</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.supplier_business_name || s.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-2">
                <label>Date From</label>
                <input type="date" name="start_date" className="form-control" value={filters.start_date} onChange={handleFilterChange} />
              </div>
              <div className="col-md-3 mb-2">
                <label>Date To</label>
                <input type="date" name="end_date" className="form-control" value={filters.end_date} onChange={handleFilterChange} />
              </div>
            </div>
            <div className="filter-actions mt-2">
              <button className="btn btn-sm btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-sm btn-primary ml-2" onClick={() => setShowFilters(false)}>Close</button>
            </div>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {isLoading ? (
          <div className="loading-sales">
            <h3>⏳ Loading Purchase Returns...</h3>
            <p>Fetching purchase returns data from server...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="no-sales">
            <h3>📊 No Purchase Returns Found</h3>
            <p>No purchase returns have been recorded yet.</p>
          </div>
        ) : (
          <>
            <div className="products-table-container">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Date</th>
                    <th>Reference No</th>
                    <th>Parent Purchase</th>
                    <th>Location</th>
                    <th>Supplier</th>
                    <th>Payment Status</th>
                    <th>Grand Total</th>
                    <th>Payment Due</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => {
                    const finalTotal = parseFloat(row.final_total || 0);
                    const isPaidStatus = row.payment_status?.toLowerCase() === 'paid';
                    const amountPaid = isPaidStatus ? finalTotal : (row.amount_paid !== undefined ? parseFloat(row.amount_paid) : (row.payment_lines ? row.payment_lines.reduce((acc, p) => acc + parseFloat(p.amount || 0), 0) : 0));
                    const due = isPaidStatus ? 0 : (row.payment_due !== undefined ? parseFloat(row.payment_due) : Math.max(0, finalTotal - amountPaid));
                    const displayStatusLabel = isPaidStatus ? 'Received' : row.payment_status;

                    return (
                      <tr
                        key={row.id}
                        onClick={(e) => {
                          if (!e.target.closest('.btn-group') && !e.target.closest('.dropdown-menu') && !e.target.closest('button')) {
                            setViewReturnId(row.id);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="actions-cell">
                          <div className="btn-group" ref={(el) => (actionButtonRefs.current[row.id] = el)}>
                            <button
                              type="button"
                              className="actions-btn action-btn-trigger dropdown-toggle"
                              onClick={(e) => { e.stopPropagation(); toggleDropdown(row.id, e); }}
                            >
                              Actions <span className="arrow-down">▼</span>
                            </button>
                            {openActionId === row.id && ReactDOM.createPortal(
                              <div
                                className="action-dropdown-menu"
                                style={{
                                  position: 'fixed',
                                  top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                  bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                  left: `${dropdownPosition.left}px`,
                                  maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                  zIndex: 9999999
                                }}
                              >
                                <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); setOpenActionId(null); setViewReturnId(row.id); }}>
                                  <i className="fa fa-eye text-info me-2"></i> View
                                </button>
                                <button className="dropdown-item" onClick={() => { setOpenActionId(null); handleEdit(row); }}>
                                  <i className="fa fa-edit text-primary me-2"></i> Edit
                                </button>
                                {row.payment_status?.toLowerCase() !== 'paid' && due > 0 && (
                                  <button className="dropdown-item" onClick={() => { setOpenActionId(null); handleAddPayment(row); }}>
                                    💳 Add payment
                                  </button>
                                )}
                                <button className="dropdown-item" onClick={() => { setOpenActionId(null); handleViewPayments(row); }}>
                                  💰 View Payments
                                </button>
                                <button className="dropdown-item delete-item" onClick={() => { setOpenActionId(null); handleDelete(row.id); }}>
                                  🗑️ Delete
                                </button>
                              </div>,
                              document.body
                            )}
                          </div>
                        </td>
                        <td>{formatDate(row.transaction_date || row.date)}</td>
                        <td>{row.ref_no || row.reference_no || 'N/A'}</td>
                        <td>{row.parent_purchase || row.parent_purchase_ref || 'N/A'}</td>
                        <td>{row.location_name || row.location?.name || 'N/A'}</td>
                        <td>{row.supplier_name || row.supplier?.name || row.contact?.name || 'N/A'}</td>
                        <td>{renderStatusBadge(displayStatusLabel || row.payment_status)}</td>
                        <td>{formatCurrency(finalTotal)}</td>
                        <td>{formatCurrency(due)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
              <div>
                Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, total)} of {total} entries
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  Previous
                </button>
                <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
                  Page {currentPage} of {lastPage}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= lastPage}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showViewPaymentsModal && (
        <ViewPaymentsModal
          transaction={selectedReturn}
          onClose={() => setShowViewPaymentsModal(false)}
          onPaymentDeleted={() => fetchData(currentPage)}
        />
      )}

      {viewReturnId && (
        <ViewPurchaseReturnModal
          returnId={viewReturnId}
          onClose={() => setViewReturnId(null)}
        />
      )}

      {showAddPaymentModal && (
        <AddPaymentModal
          sale={selectedReturn}
          onClose={() => setShowAddPaymentModal(false)}
          onSuccess={() => {
            setShowAddPaymentModal(false);
            fetchData(currentPage);
          }}
        />
      )}

    </div>
  );
};

export default ListPurchaseReturn;
